'use client';
import {
	Alert,
	Button,
	Checkbox,
	Icon,
	Input,
	LoadingIndicator,
	TypeAhead,
} from '@fshr/components';
import { useI18n } from '@fshr/context/i18n-context';
import { ERROR_CODES } from '@fshr/shared';
import { rumJourneyTransaction } from '@fshr/shared/rum';
import rum from '@fshr/shared-react/rum';
import { useForm } from '@fshr/hooks';
import { useState } from 'react';
import { ServerActionError } from '@fshr/shared-react/server';
import { ensureDomainSafeLink, getConfig } from '@fshr/shared-react';
import { findRoomReservation } from '../actions.server';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTealium } from '@fshr/context/tealium-context';
import { useProfile } from '@profiles/lib/context/profile-context';
import { observer } from 'mobx-react-lite';
import { isContentSegment } from '../../upcoming-trip/_helpers/segments';

const FindAReservationForm = ({
	hotelOptions,
	additionalRouterSegment,
	initialValues = {
		hotelName: '',
		confirmationNumber: '',
	},
}: {
	hotelOptions: { value: string; label: string; propertyId?: string }[];
	additionalRouterSegment?: string;
	initialValues?: { hotelName: string; confirmationNumber: string };
}) => {
	const router = useRouter();
	const searchParams = useSearchParams();
	let redirect: string | undefined | null = undefined;
	if (typeof window !== 'undefined') {
		redirect = ensureDomainSafeLink(
			searchParams.get('afterFindReservationRedirectTo'),
			window.location
		);
	}
	const { t, localeLink, locale } = useI18n();
	const { trackLink } = useTealium();
	const { NEXT_PUBLIC_WEBSITE_BASE_URL } = getConfig();
	const { globalStore } = useProfile();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<{ type?: 'functional' | 'technical'; message?: string }>({});

	const handleFormSubmit = async (formValues: Record<string, string>): Promise<void> => {
		let couldNotRetrieve;
		let reservationCancelled;
		let isFunctionalError;

		const transaction = rumJourneyTransaction('Submit Find a Reservation Form', {
			fs_journey: 'find_a_reservation',
			fs_step: 'submit_lookup',
			fs_source: 'web',
			fs_route: window.location.pathname,
		});
		try {
			setIsSubmitting(true);
			setError({});

			const [e, roomReservation] = await findRoomReservation(
				formValues.lastName,
				formValues.hotelName,
				formValues.confirmationNumber
			);

			if (e) {
				throw e;
			}

			if (roomReservation?.UserToken) {
				transaction.succeed();
				trackLink(
					'form_submit',
					initialValues.hotelName && initialValues.confirmationNumber
						? {
								form_name: 'confirm reservation details',
								login_state: 'Partially Authenticated',
							}
						: {
								form_name: 'find a reservation',
							}
				);
				if (roomReservation.roomReservation?.orsConfirmationNumber) {
					globalStore?.setActiveBooking(roomReservation.roomReservation?.orsConfirmationNumber);
				}
				//Redirect to upcoming trip for the entered values
				const hasContentSegments = isContentSegment(additionalRouterSegment);

				const { propertyId } =
					hotelOptions.find(hotelOption => hotelOption.value === formValues.hotelName) ?? {};
				if (propertyId && hasContentSegments) {
					const language = locale ?? 'en';

					router.push(
						redirect ||
							`/${language}/${propertyId}${additionalRouterSegment}${window.location.search}`
					);

					return;
				}

				router.push(
					redirect ||
						`${localeLink(
							`/upcoming-trip/${formValues.hotelName}/${formValues.confirmationNumber}${additionalRouterSegment?.replace('/', '') === 'itinerary' ? additionalRouterSegment?.replace('/', '#') : additionalRouterSegment}`
						)}${window.location.search}`
				);
			} else {
				throw new Error('missing token');
			}
		} catch (e) {
			couldNotRetrieve = (e as ServerActionError).errorCode === ERROR_CODES.COULD_NOT_RETRIEVE;
			reservationCancelled =
				(e as ServerActionError).errorCode === ERROR_CODES.RESERVATION_CANCELLED;
			isFunctionalError = couldNotRetrieve || reservationCancelled;

			if (!isFunctionalError) {
				rum?.captureError(
					e instanceof Error
						? e
						: new Error(`Find a reservation lookup failed: ${(e as ServerActionError).errorCode}`)
				);
			}

			const errorCode = (e as ServerActionError).errorCode;

			transaction.fail(
				reservationCancelled
					? 'business_rejection'
					: couldNotRetrieve
						? 'validation'
						: 'technical_failure',
				errorCode,
				{
					// Labels from before GEM-208; existing dashboards read them.
					errorType: reservationCancelled
						? 'cancelled'
						: couldNotRetrieve
							? 'validation'
							: 'technical',
					...(errorCode ? { errorCode } : {}),
					error: e instanceof Error ? e.message : 'Unexpected Error',
				}
			);

			if (couldNotRetrieve) {
				setError({
					type: 'functional',
					message: t(
						'This information you entered does not match a current reservation in our system. Please check the information entered and try again. For assistance, please contact the <a href="{0}">Hotel or Resort</a> directly.',
						[`${NEXT_PUBLIC_WEBSITE_BASE_URL}${localeLink('/find_a_hotel_or_resort/')}`]
					),
				});
			} else if (reservationCancelled) {
				setError({
					type: 'functional',
					message: t(
						'This reservation has been canceled. Please call Worldwide Reservations Office at 1-800-819-5053 for assistance.'
					),
				});
			} else {
				setError({
					type: 'technical',
					message: t('An unexpected error occurred. Please try again later.'),
				});
			}
		} finally {
			if (isFunctionalError) {
				scrollTo({
					top: 0,
					behavior: 'smooth',
				});
			}
			setIsSubmitting(false);
		}
	};

	const { handleSubmit, registerField } = useForm({ onSubmit: handleFormSubmit });

	return (
		<>
			{isSubmitting && <LoadingIndicator className="opacity-50" />}
			<form noValidate={true} className="flex flex-col h-full" onSubmit={handleSubmit}>
				<div className="flex flex-col gap-200 w-full h-full">
					<div className="grid grid-cols-1 w-full">
						{error.type === 'functional' && error.message && (
							<div className="mb-300">
								<Alert intent="error" kind="subtle" icon={<Icon icon="alert" />}>
									<span
										className="text-caption [&_a]:underline"
										dangerouslySetInnerHTML={{
											__html: error.message,
										}}
									></span>
								</Alert>
							</div>
						)}

						<Input
							id="confirmationNumber"
							label={t('Confirmation Number')}
							placeholder={t('Confirmation Number')}
							{...registerField({
								name: 'confirmationNumber',
								initialValue: initialValues.confirmationNumber,
								options: {
									required: true,
									errorMessages: { empty: t('Confirmation Number is required.') },
								},
							})}
						/>

						<Input
							id="lastName"
							label={t('Last Name')}
							placeholder={t('Last Name')}
							{...registerField({
								name: 'lastName',
								initialValue: '',
								options: {
									required: true,
									errorMessages: { empty: t('Last Name is required.') },
								},
							})}
						/>

						<TypeAhead
							id="hotelName"
							label={t('Hotel Name')}
							placeholder={t('Hotel Name')}
							noResults={t('No results')}
							options={hotelOptions}
							{...registerField({
								name: 'hotelName',
								initialValue: hotelOptions.find(({ value }) => value === initialValues.hotelName)
									? initialValues.hotelName
									: '',
								type: 'typeahead',
								options: {
									required: true,
									errorMessages: {
										empty: t('Hotel Name is required.'),
										partial: t('Please select a valid hotel name.'),
									},
								},
							})}
						/>
					</div>
					<div className="flex flex-col gap-300 w-full flex-1 ">
						<div className="mt-[5px]">
							<Checkbox
								id="confirmation-checkbox"
								{...registerField({
									name: 'confirmation',
									initialValue: '',
									type: 'checkbox',
									options: {
										required: true,
										errorMessages: {
											empty: t('To proceed, please accept terms and conditions.'),
										},
									},
								})}
								label={
									<label htmlFor="confirmation-checkbox" className="text-body3">
										{t(
											'I confirm that I am the guest, another individual authorized and responsible for the requested reservation and payment details, or I have otherwise been authorized to update this reservation.'
										)}
									</label>
								}
							/>
						</div>
						{error.type === 'technical' && (
							<Alert intent="error" kind="subtle" icon={<Icon icon="alert" />}>
								<span className="text-caption">{error.message}</span>
							</Alert>
						)}
						<div className="flex gap-200 justify-end items-center w-full">
							<Button type="submit" intent="filled" className="w-full">
								{t('Find Reservation')}
							</Button>
						</div>
					</div>
				</div>
			</form>
		</>
	);
};

export default observer(FindAReservationForm);
